import { IconButton, Stack, TextField } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import { Button } from "@platform/design-system";

import type { DraftDollarAmount } from "@platform/plan-grid-core";
import type { ReactElement } from "react";

/** Matches the shared salaryMultiplier cell spec's upper bound. */
export const MAX_SALARY_MULTIPLIER = 100;

/**
 * The shared salaryMultiplier cell spec stores two decimal places, so the
 * smallest multiplier it can represent is the input's step and its floor —
 * a zero multiplier is no benefit at all.
 */
export const SALARY_MULTIPLIER_STEP = 0.01;

export const isValidSalaryMultiplier = (entry: DraftDollarAmount): boolean => {
  const value = Number(entry.amount);

  return (
    entry.amount !== "" &&
    value >= SALARY_MULTIPLIER_STEP &&
    value <= MAX_SALARY_MULTIPLIER
  );
};

interface SalaryMultipliersFieldProps {
  entries: DraftDollarAmount[];
  showErrors: boolean;
  maxEntries: number;
  onUpdate: (index: number, value: string) => void;
  onAdd: () => void;
  onRemove: (index: number) => void;
}

/**
 * Controlled list of salary multipliers with add/remove, used by
 * BenefitDescriptionCellEditor for the "multiple salary multipliers" type.
 */
export const SalaryMultipliersField = ({
  entries,
  showErrors,
  maxEntries,
  onUpdate,
  onAdd,
  onRemove,
}: SalaryMultipliersFieldProps): ReactElement => (
  <>
    {entries.map((entry, index) => (
      <Stack key={entry.id} direction="row" spacing={1} alignItems="center">
        <TextField
          size="small"
          type="number"
          label="Salary Multiplier"
          value={entry.amount}
          onChange={(e) => onUpdate(index, e.target.value)}
          error={showErrors && !isValidSalaryMultiplier(entry)}
          inputProps={{
            min: SALARY_MULTIPLIER_STEP,
            max: MAX_SALARY_MULTIPLIER,
            step: SALARY_MULTIPLIER_STEP,
          }}
          fullWidth
        />

        <IconButton
          size="small"
          aria-label="Remove salary multiplier"
          onClick={() => onRemove(index)}
          disabled={entries.length === 1}
        >
          <CloseOutlinedIcon fontSize="small" />
        </IconButton>
      </Stack>
    ))}

    {entries.length < maxEntries && (
      <Button
        size="sm"
        variant="tertiary"
        startIcon={<AddIcon />}
        onClick={onAdd}
        sx={{ alignSelf: "flex-start" }}
      >
        Add multiplier
      </Button>
    )}
  </>
);
